import { FC } from 'react';

import { ChannelBalanceSummary } from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import {
  DELIVERY_CHANNEL_CONFIG,
  DELIVERY_CHANNEL_LIST,
  DeliveryChannel,
  formatDeliveryChannelSelectLabel,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import { DeliverModalState } from '../hooks/useRedemptionPage';

const DELIVERY_CHANNEL_SELECT_OPTIONS: Array<{
  value: DeliveryChannel | '';
  label: string;
}> = [
  { value: '', label: '발송 매체 선택' },
  ...DELIVERY_CHANNEL_LIST.map((ch) => ({
    value: ch,
    label: formatDeliveryChannelSelectLabel(ch),
  })),
];

interface Props {
  deliverModal: DeliverModalState;
  setDeliverModal: (modal: DeliverModalState | null) => void;
  onConfirm: () => void;
  onReject: (couponId: string) => void;
  /** 충전금 미러 원장 잔액 — null이면(구서버 404) 잔액 영역 미노출 */
  channelBalances?: ChannelBalanceSummary[] | null;
}

export const DeliverModal: FC<Props> = ({
  deliverModal,
  setDeliverModal,
  onConfirm,
  onReject,
  channelBalances,
}) => {
  const count = deliverModal.couponIds.length;
  const wholeNum = Number(deliverModal.priceWhole);
  const hasValidWhole =
    deliverModal.priceWhole !== '' && !isNaN(wholeNum) && wholeNum > 0;
  const totalAmount = hasValidWhole ? wholeNum * count : 0;

  // 선택 매체의 미러 잔액 — 발송 직전 실수(도매가 오입력·충전 부족)를 잡는 핵심 대조값
  const selectedBalance =
    deliverModal.deliveryChannel && channelBalances
      ? channelBalances.find((b) => b.channel === deliverModal.deliveryChannel)
      : undefined;
  const afterBalance =
    selectedBalance && hasValidWhole ? selectedBalance.balance - totalAmount : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={() => setDeliverModal(null)} />
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-gray-900">
          발송 처리
          {deliverModal.isBulk ? ` (${deliverModal.couponIds.length}건)` : ''}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          가격 정보를 입력한 후 발송 처리하세요.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              발송 매체 (deliveryChannel) <span className="text-red-500">*</span>
            </label>
            <select
              value={deliverModal.deliveryChannel}
              onChange={(e) =>
                setDeliverModal({
                  ...deliverModal,
                  deliveryChannel: e.target.value as DeliveryChannel | '',
                })
              }
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {DELIVERY_CHANNEL_SELECT_OPTIONS.map((opt) => (
                <option
                  key={opt.value === '' ? '__none__' : opt.value}
                  value={opt.value}
                  disabled={opt.value === ''}
                >
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              판매가 (priceRetail)
            </label>
            <div className="mt-1 flex items-center rounded-md border bg-gray-50 px-3 py-2 text-sm text-gray-500">
              {deliverModal.priceRetail.toLocaleString()}원
              <span className="ml-2 text-xs text-gray-400">(자동)</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              도매가 (priceWhole)
            </label>
            <input
              type="number"
              value={deliverModal.priceWhole}
              onChange={(e) =>
                setDeliverModal({
                  ...deliverModal,
                  priceWhole: e.target.value,
                  // 도매가가 바뀌면 총액도 바뀌므로 금액 확인을 다시 받아야 함
                  amountConfirmed: false,
                })
              }
              placeholder="도매가 입력"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              리베이트 (priceRebate){' '}
              <span className="text-xs font-normal text-gray-400">
                (선택 · 미입력 시 0)
              </span>
            </label>
            <input
              type="number"
              value={deliverModal.priceRebate}
              onChange={(e) =>
                setDeliverModal({ ...deliverModal, priceRebate: e.target.value })
              }
              placeholder="0"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
            {hasValidWhole ? (
              <>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-medium text-gray-700">총 발주비용</span>
                  <span className="text-lg font-bold text-amber-700">
                    {totalAmount.toLocaleString()}원
                  </span>
                </div>
                <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-800">
                  <input
                    type="checkbox"
                    checked={deliverModal.amountConfirmed}
                    onChange={(e) =>
                      setDeliverModal({
                        ...deliverModal,
                        amountConfirmed: e.target.checked,
                      })
                    }
                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>이 금액이 맞습니다.</span>
                </label>
              </>
            ) : (
              <p className="text-sm text-gray-500">도매가 입력 필요</p>
            )}
          </div>

          {selectedBalance && (
            <div
              className={`rounded-md border p-3 text-sm ${
                afterBalance !== null && afterBalance < 0
                  ? 'border-red-300 bg-red-50'
                  : 'border-indigo-200 bg-indigo-50/60'
              }`}
            >
              <div className="flex items-baseline justify-between">
                <span className="text-gray-600">
                  현재 {DELIVERY_CHANNEL_CONFIG[selectedBalance.channel].label} 잔액
                </span>
                <span
                  className={`font-semibold ${
                    selectedBalance.balance < 0 ? 'text-red-600' : 'text-gray-900'
                  }`}
                >
                  {selectedBalance.balance.toLocaleString()}원
                </span>
              </div>
              {afterBalance !== null ? (
                <div className="mt-1 flex items-baseline justify-between">
                  <span className="text-gray-600">발송 후 잔액</span>
                  <span
                    className={`font-bold ${
                      afterBalance < 0 ? 'text-red-600' : 'text-indigo-700'
                    }`}
                  >
                    {afterBalance.toLocaleString()}원
                  </span>
                </div>
              ) : (
                <p className="mt-1 text-xs text-gray-400">
                  도매가를 입력하면 발송 후 잔액이 표시됩니다.
                </p>
              )}
              {afterBalance !== null && afterBalance < 0 && (
                <p className="mt-1 text-xs font-medium text-red-600">
                  잔액 부족 — 도매가 오입력이 아닌지, 충전·입금 기록이 빠지지 않았는지
                  확인하세요. (발송이 차단되지는 않습니다)
                </p>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          {!deliverModal.isBulk ? (
            <button
              onClick={() => {
                const couponId = deliverModal.couponIds[0] ?? '';
                setDeliverModal(null);
                onReject(couponId);
              }}
              className="text-xs text-orange-600 hover:text-orange-800 hover:underline"
            >
              발송불가 처리
            </button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <button
              onClick={() => setDeliverModal(null)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              취소
            </button>
            <button
              onClick={onConfirm}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              발송 처리
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
